import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import classnames from 'classnames';
import moment from 'moment';

import Button from '@components/Button';
import { Dropdown } from '@components/Dropdown';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';
import Toggle from '@components/Toggle';

import {
  createDelegateWorkConfig,
  fetchInspectionAssignees,
  getCRMAssignees,
  updateDelegateWorkConfig,
} from '@actions/user';

import { getEmptyErrorState } from '@utils/AppUtil';
import { hasProductCEAccess, hasProductCRMAccess } from '@utils/AuthUtil';
import { getDateAfter, getDateAfterString, isAfter, isBefore } from '@utils/CalendarUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import { getFormattedDeviceDetails } from '@utils/DeviceDetailsUtil';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';
import { track } from '@utils/MixPanelUtils';

import './style.scss';

export interface IDelegateWorkModalProps {
  show: boolean;
  onClose: (e: React.SyntheticEvent<HTMLAnchorElement>) => void;
  configViolationTypes: IConfigXerceViolationTypeResponse[];
  isEdit: boolean;
  // from redux
  delegateWorkConfig: IDelegateWorkResponse;
  crmAssignees: ICSUser[];
  assignees: ICaseUserResponse[];
  createDelegateWorkConfig: TCreateDelegateWorkConfig;
  updateDelegateWorkConfig: TUpdateDelegateWorkConfig;
  fetchInspectionAssignees: TFetchInspectionAssignees;
  getCRMAssignees: TGetCRMAssignee;
  userProfile?: IUserProfile;
}

export interface IDelegateWorkModalState {
  delegateWorkConfig: IDelegateWorkRequest;
  error: {
    caseAssigneeId: IFieldValidation;
    inspectionAssigneeId: IFieldValidation;
    submissionAssigneeId: IFieldValidation;
    instruction: IFieldValidation;
  };
}

const cyberdyneValidator = new CyberdyneValidator();
const errMsgGenerator = new ErrorMessageGenerator();

export class DelegateWorkModal extends React.Component<IDelegateWorkModalProps, IDelegateWorkModalState> {
  constructor(props: IDelegateWorkModalProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidUpdate(prevProps: IDelegateWorkModalProps) {
    const {
      delegateWorkConfig,
      configViolationTypes,
      show,
      fetchInspectionAssignees: fetchInspectionAssigneesProp,
      isEdit,
      userProfile,
    } = this.props;

    const hasOpened = show && prevProps.show !== show;

    if (hasOpened) {
      if (isEdit) {
        this.setState({
          delegateWorkConfig,
          error: this.getEmptyState().error,
        });
      } else {
        this.setState(this.getEmptyState());
      }
    }

    if (hasOpened && userProfile?.scopes.crm && userProfile?.scopes.crm.isActive && !prevProps.crmAssignees?.length) {
      this.fetchCRMAssignees();
    }

    const shouldFetchInspectionAssignees =
      (hasOpened && hasProductCEAccess(userProfile)) || (!prevProps.configViolationTypes && !!configViolationTypes);

    if (shouldFetchInspectionAssignees && configViolationTypes?.length) {
      const violationTypeIds = configViolationTypes.filter((violation) => violation.isActive).map((v) => v.id);

      if (violationTypeIds.length) {
        fetchInspectionAssigneesProp(violationTypeIds);
      }
    }

    if (JSON.stringify(prevProps.delegateWorkConfig) !== JSON.stringify(this.props.delegateWorkConfig)) {
      if (configViolationTypes && configViolationTypes.length) {
        const violationTypeIds = configViolationTypes.filter((violation) => violation.isActive).map((v) => v.id);

        if (violationTypeIds.length) {
          fetchInspectionAssigneesProp(violationTypeIds);
        }
      }
    }
  }

  fetchCRMAssignees() {
    const params: IGetCRMAssigneeParams = {
      status: CRMAssigneeStatus.ACTIVE,
      permission: [CRMACL.BASIC, CRMACL.OVERWRITE],
    };
    this.props.getCRMAssignees(params);
  }

  getEmptyState = (id = null): IDelegateWorkModalState => ({
    delegateWorkConfig: {
      isActive: false,
      caseAssigneeId: null,
      endDate: getDateAfterString(0, DateUnit.DAYS),
      startDate: getDateAfterString(0, DateUnit.DAYS),
      inspectionAssigneeId: null,
      instruction: '',
      submissionAssigneeId: null,
      id,
    },
    error: {
      caseAssigneeId: getEmptyErrorState(),
      inspectionAssigneeId: getEmptyErrorState(),
      submissionAssigneeId: getEmptyErrorState(),
      instruction: getEmptyErrorState(),
    },
  });

  handleChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    this.setState((prevState) => ({
      ...prevState,
      delegateWorkConfig: {
        ...prevState.delegateWorkConfig,
        [name]: value,
      },
    }));
  };

  handleStartDateChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const { value: startDate } = event.currentTarget;
    const { endDate } = this.state.delegateWorkConfig;

    this.setState((prevState) => ({
      ...prevState,
      delegateWorkConfig: {
        ...prevState.delegateWorkConfig,
        ...(isBefore(endDate, startDate)
          ? {
              startDate,
              endDate: getDateAfterString(0, DateUnit.DAYS, moment(startDate)),
            }
          : { startDate }),
      },
    }));
  };

  handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isActive = e.target.checked;
    const { isEdit, delegateWorkConfig } = this.props;

    if (!isActive) {
      const id = isEdit ? delegateWorkConfig.id : null;

      this.setState(this.getEmptyState(id));
      track('DELEGATE_WORK_TOGGLE_OFF', {
        'Device Details': getFormattedDeviceDetails(),
      });
    } else {
      track('DELEGATE_WORK_TOGGLE_ON', {
        'Device Details': getFormattedDeviceDetails(),
      });
      this.setState((prevState) => ({
        ...prevState,
        delegateWorkConfig: {
          ...prevState.delegateWorkConfig,
          isActive,
        },
      }));
    }
  };

  userDatesOverlap = (userStartDate, userEndDate) => {
    const { startDate, endDate } = this.state.delegateWorkConfig;

    const userStartDateTime = new Date(userStartDate).getTime();
    const userEndDateTime = new Date(userEndDate).getTime();
    const startDateTime = new Date(startDate).getTime();
    const endDateTime = new Date(endDate).getTime();

    const isOverlap = !(
      (isBefore(userStartDateTime, startDateTime) && isBefore(userEndDateTime, startDateTime)) ||
      (isAfter(userStartDateTime, endDateTime) && isAfter(userEndDateTime, endDateTime))
    );

    return isOverlap;
  };

  getUserOptions = (assignees: ICSUser[] | ICaseUserResponse[], selectedAssignee: ICSUser | ICaseUserResponse) => {
    const { isEdit } = this.props;
    const crmAssigneesOptions = [];

    // Process assignees only if not null/undefined
    if (assignees) {
      for (const assignee of assignees) {
        if (assignee.id !== this.props.userProfile.id) {
          if (assignee.workDelegation && !isEdit) {
            const { startDate, endDate } = assignee.workDelegation;

            if (!this.userDatesOverlap(startDate, endDate)) {
              const option = {
                value: assignee.id,
                label: `${assignee.firstName} ${assignee.lastName}`,
              };

              crmAssigneesOptions.push(option);
            }
          } else {
            const option = {
              value: assignee.id,
              label: `${assignee.firstName} ${assignee.lastName}`,
            };

            crmAssigneesOptions.push(option);
          }
        }
      }
    }

    if (selectedAssignee && !selectedAssignee.isActive) {
      const option: IPickListOptions = {
        value: selectedAssignee.id,
        label: `${selectedAssignee.firstName} ${selectedAssignee.lastName}`,
        isDisabled: true,
      };

      crmAssigneesOptions.push(option);
    }

    return crmAssigneesOptions;
  };

  renderBody = () => {
    const {
      delegateWorkConfig: {
        isActive,
        startDate,
        endDate,
        instruction,
        caseAssigneeId,
        inspectionAssigneeId,
        submissionAssigneeId,
      },
      error,
    } = this.state;

    const { assignees, crmAssignees, delegateWorkConfig } = this.props;

    return (
      <>
        <Row className={classnames('out-of-office-dates')}>
          <Col md={3} className="field-section">
            <Toggle bsStyle="primary" onToggle={this.handleToggle} toggleState={isActive} inverted>
              Out of Office
            </Toggle>
          </Col>
          {isActive && (
            <>
              <Input
                field={{
                  label: 'Start Date',
                  value: startDate,
                  isRequired: false,
                  type: CustomFieldType.DATE,
                }}
                datePickerOptions={{
                  constraints: {
                    min: getDateAfter(-1, DateUnit.DAYS),
                  },
                }}
                name="startDate"
                width={3}
                onChange={this.handleStartDateChange}
              />
              <Input
                field={{
                  label: 'End Date',
                  value: endDate,
                  isRequired: false,
                  type: CustomFieldType.DATE,
                }}
                datePickerOptions={{
                  constraints: {
                    min: getDateAfter(-1, DateUnit.DAYS, moment(startDate)),
                  },
                }}
                name="endDate"
                width={3}
                onChange={this.handleChange}
              />
            </>
          )}
        </Row>
        {isActive && (
          <>
            {hasProductCEAccess(this.props.userProfile) && (
              <>
                <Row className="field-section assignee-options">
                  <span className="dropdown-label">Assign Cases To:</span>
                  <Dropdown
                    name="caseAssigneeId"
                    id="caseAssigneeId"
                    placeholder="Unassigned"
                    value={caseAssigneeId}
                    options={this.getUserOptions(assignees, delegateWorkConfig && delegateWorkConfig.caseAssignee)}
                    onChange={this.handleChange}
                    className={classnames({
                      'option-selected': caseAssigneeId,
                    })}
                    width="240px"
                    searchMode={true}
                    searchPlaceholder="Search..."
                    error={error.caseAssigneeId}
                    type={DropdownType.BORDER}
                  />
                </Row>
                <Row className="field-section assignee-options">
                  <span className="dropdown-label">Assign Inspections To:</span>
                  <Dropdown
                    name="inspectionAssigneeId"
                    id="inspectionAssigneeId"
                    placeholder="Unassigned"
                    value={inspectionAssigneeId}
                    options={this.getUserOptions(
                      assignees,
                      delegateWorkConfig && delegateWorkConfig.inspectionAssignee
                    )}
                    onChange={this.handleChange}
                    className={classnames({
                      'option-selected': inspectionAssigneeId,
                    })}
                    width="240px"
                    searchMode={true}
                    searchPlaceholder="Search..."
                    error={error.inspectionAssigneeId}
                    type={DropdownType.BORDER}
                  />
                </Row>
              </>
            )}
            {hasProductCRMAccess(this.props.userProfile) && (
              <Row className="field-section assignee-options">
                <span className="dropdown-label">Assign Submissions To:</span>
                <Dropdown
                  name="submissionAssigneeId"
                  id="submissionAssigneeId"
                  placeholder="Unassigned"
                  value={submissionAssigneeId}
                  options={this.getUserOptions(
                    crmAssignees,
                    delegateWorkConfig && delegateWorkConfig.submissionAssignee
                  )}
                  onChange={this.handleChange}
                  className={classnames({
                    'option-selected': submissionAssigneeId,
                  })}
                  width="240px"
                  searchMode={true}
                  searchPlaceholder="Search..."
                  error={error.submissionAssigneeId}
                  type={DropdownType.BORDER}
                />
              </Row>
            )}
            <Input
              field={{
                label: 'Add Instructions',
                type: CustomFieldType.TEXTAREA,
                isRequired: false,
                value: instruction,
              }}
              name="instruction"
              onChange={this.handleChange}
              error={error.instruction}
            />
          </>
        )}
      </>
    );
  };

  validateForm = () => {
    let isValid = true;
    const { caseAssigneeId, inspectionAssigneeId, instruction, isActive, submissionAssigneeId } =
      this.state.delegateWorkConfig;
    const error = this.getEmptyState().error;

    if (isActive) {
      if (hasProductCEAccess(this.props.userProfile) && !caseAssigneeId) {
        error.caseAssigneeId = {
          errorState: IValidationState.ERROR,
          errorMessage: errMsgGenerator.getMandatoryFieldMessage('Case Assignee'),
        };

        isValid = false;
      }

      if (hasProductCEAccess(this.props.userProfile) && !inspectionAssigneeId) {
        error.inspectionAssigneeId = {
          errorState: IValidationState.ERROR,
          errorMessage: errMsgGenerator.getMandatoryFieldMessage('Inspection Assignee'),
        };

        isValid = false;
      }

      if (hasProductCRMAccess(this.props.userProfile) && !submissionAssigneeId) {
        error.submissionAssigneeId = {
          errorState: IValidationState.ERROR,
          errorMessage: errMsgGenerator.getMandatoryFieldMessage('Submission Assignee'),
        };

        isValid = false;
      }

      if (cyberdyneValidator.isStringGreaterThanLength(instruction, 500)) {
        error.instruction = {
          errorState: IValidationState.ERROR,
          errorMessage: errMsgGenerator.getCharacterLimitMessage('Instruction', 500),
        };

        isValid = false;
      }
    }

    this.setState({
      error,
    });

    return isValid;
  };

  onSubmit = (e: React.SyntheticEvent<HTMLAnchorElement>) => {
    const { delegateWorkConfig } = this.state;
    const { isEdit } = this.props;

    if (this.validateForm()) {
      if (isEdit) {
        this.props.updateDelegateWorkConfig(delegateWorkConfig);
      } else {
        this.props.createDelegateWorkConfig(delegateWorkConfig);
      }

      track('DELEGATE_WORK_SAVED', {
        'Device Details': getFormattedDeviceDetails(),
      });
      this.props.onClose(e);
    }
  };

  renderFooter = () => (
    <>
      <Button onClick={this.props.onClose} bsStyle={ButtonStyle.DEFAULT} content="Cancel" />
      <Button onClick={this.onSubmit} bsStyle={ButtonStyle.PRIMARY} content="Save" />
    </>
  );

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.props.onClose}
        titleElement="Delegate Work (Out Of Office)"
        bodyElement={this.renderBody()}
        footerElement={this.renderFooter()}
        className={classnames('work-delegation-form', 'modal--min-height')}
      />
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  assignees: state.user.assignees,
  crmAssignees: state.user.crmAssignees,
  userProfile: state.auth.userProfile,
});

const mapDispatchToProps = (dispatch: any) => ({
  updateDelegateWorkConfig: (delegateWorkRequest: IDelegateWorkRequest) =>
    dispatch(updateDelegateWorkConfig(delegateWorkRequest)),
  createDelegateWorkConfig: (delegateWorkRequest: IDelegateWorkRequest) =>
    dispatch(createDelegateWorkConfig(delegateWorkRequest)),
  fetchInspectionAssignees: (violationIds: number[]) => dispatch(fetchInspectionAssignees(violationIds)),
  getCRMAssignees: (params: IGetCRMAssigneeParams) => dispatch(getCRMAssignees(params, true, true)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DelegateWorkModal);
